import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import classnames from 'classnames';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { TrackingHandler } from 'client/tracking/handler';
import { getCreativeId } from 'client/engagement-handlers/helper';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { FAIR_DEAL, DEAL_COVERAGE } from 'site-modules/shared/components/deal/deal-constants';
import { isTexas } from 'site-modules/shared/utils/inventory-utils/is-texas';
import { TrackingConstant } from 'client/tracking/constant';
import { getSavingsText } from 'site-modules/shared/utils/inventory-utils/get-savings-text';
import { InfoTooltip } from 'site-modules/shared/components/inventory/info-tooltip/info-tooltip';

export function DealPopover({
  condition,
  trackingId,
  type,
  stateCode,
  savings = null,
  isDisplayMarketInfo = false,
  isSavingsPresent = false,
  tooltipClasses = 'py-0_75 px-0_5',
}) {
  const popover = useRef();
  const onDealTypeTooltipOpen = () => {
    TrackingHandler.fireEvent(trackingId, {
      event_data: {
        action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
        creative_id: getCreativeId(get(popover, 'current')),
      },
    });
  };

  const getDefaultPopoverBodyText = inventoryType =>
    `of the ${inventoryType} inventory currently listed on Edmunds is a ${type.toLowerCase()} price. `;

  const getPopoverBody = inventoryType => {
    const dealCoverage = DEAL_COVERAGE[inventoryType][type];
    const popoverBodyText = getDefaultPopoverBodyText(inventoryType);

    if (dealCoverage && popoverBodyText) {
      return `${dealCoverage} ${popoverBodyText} `;
    }

    return '';
  };

  const getPopoverUsedTitle = () => {
    let popoverTitle = '';
    const dealLabel = `${type} Price`;
    const isSavings = isDisplayMarketInfo ? isSavingsPresent : !!savings; // the check is for savings=0 - it should display when isDisplayMarketInfo===true
    if (type === FAIR_DEAL) {
      popoverTitle = `Edmunds considers this used vehicle a ${dealLabel} because the price is within the market average for similar vehicles in your region.`;
    } else if (!isTexas(stateCode) && isSavings) {
      popoverTitle = `Edmunds considers this used vehicle a ${dealLabel} because the ${
        isDisplayMarketInfo ? 'savings' : 'price'
      } is ${isDisplayMarketInfo ? getSavingsText(savings) : `${formatPriceString(savings)} below market`}.`;
    } else {
      popoverTitle = `Edmunds considers this used car a ${dealLabel}.`;
    }
    return popoverTitle;
  };

  const getPopoverNewTitle = () => {
    const dealLabel = `${type} Deal`;
    return `Edmunds considers this new car a ${dealLabel}.`;
  };

  const generatePopoverCopy = () => ({
    title: isUsed(condition) ? getPopoverUsedTitle() : getPopoverNewTitle(),
    body: getPopoverBody(isUsed(condition) ? 'used' : 'new'),
  });

  const popoverCopy = generatePopoverCopy(condition);

  return (
    <div className="deal-popover d-inline-block" ref={popover}>
      <InfoTooltip
        id="deal-type-tooltip"
        className="top-0 size-10 align-middle"
        item={{
          text: (
            <span className={classnames('d-block disclaimer-popover', tooltipClasses)}>
              <span className="d-block mb-1 medium fw-bold">{popoverCopy.title}</span>
              <span className="d-block m-0 medium">
                {popoverCopy.body}Our ratings are accurate and up-to-date – we&#39;ve analyzed thousands of similar
                transactions and listings to rate these deals.
              </span>
            </span>
          ),
          placement: 'bottom',
        }}
        onTooltipOpen={onDealTypeTooltipOpen}
        screenReaderTooltipLabel="deal type"
      />
    </div>
  );
}

DealPopover.propTypes = {
  type: PropTypes.string.isRequired,
  stateCode: PropTypes.string.isRequired,
  condition: PropTypes.string.isRequired,
  trackingId: PropTypes.string.isRequired,
  savings: PropTypes.number,
  isDisplayMarketInfo: PropTypes.bool,
  isSavingsPresent: PropTypes.bool,
  tooltipClasses: PropTypes.string,
};
